// (C) Copyright 2015 Moodle Pty Ltd.
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import { Injectable } from '@angular/core';

import { CoreContentLinksHandlerBase } from '@features/contentlinks/classes/base-handler';
import { CoreContentLinksAction } from '@features/contentlinks/services/contentlinks-delegate';
import { CoreNavigator } from '@services/navigator';
import { makeSingleton } from '@singletons';
import { WP_CUSTOM_PAGES_MAIN_MENU_HOME_PAGE_NAME } from '../../constants';
import { WorkplaceCustompage } from '../custompage';
import { CoreMainMenuHomeDelegate } from '@features/mainmenu/services/home-delegate';
import { CoreLoadings } from '@services/loadings';

/**
 * Handler to treat links to custom pages.
 */
@Injectable({ providedIn: 'root' })
export class WorkplaceCustomPageLinkHandlerService extends CoreContentLinksHandlerBase {

    name = 'WorkplaceCustomPageLinkHandler';
    pattern = /\/admin\/tool\/custompage\/view\.php\?id=(\d+)/;

    /**
     * @inheritdoc
     */
    async getActions(siteIds: string[], url: string): Promise<CoreContentLinksAction[]> {
        const matches = url.match(this.pattern);
        if (!matches || matches.length < 2) {
            return [];
        }

        const pageId = matches[1];

        return [{
            action: async (siteId: string): Promise<void> => {
                const modal = await CoreLoadings.show();

                try {
                    // First make sure custom pages are registered.
                    await WorkplaceCustompage.ready();

                    // Now make sure the handlers are ready.
                    await CoreMainMenuHomeDelegate.getHandlersWhenLoaded();

                    await CoreNavigator.navigateToSitePath(`/main/home/${WP_CUSTOM_PAGES_MAIN_MENU_HOME_PAGE_NAME}/${pageId}`, {
                        preferCurrentTab: false,
                        siteId,
                    });
                } finally {
                    modal.dismiss();
                }
            },
        }];
    }

}

export const WorkplaceCustomPageLinkHandler = makeSingleton(WorkplaceCustomPageLinkHandlerService);
